import type { PersistenceDatabase, PersistenceManager } from '@tadpoles/persistence'

import type { UserContext } from '../../context/user-context.js'
import { createFamilyPersistenceManager } from './family-persistence-manager.js'
import {
    AttachmentMetadataCollectionDefinition,
    type FamilyAttachmentMetadataStore
} from './stores/attachment-metadata-store.js'
import {
    AuthenticationContextCollectionDefinition,
    FamilyAuthenticationContextStoreAdapter,
    type FamilyAuthenticationContextStore
} from './stores/authentication-context-store.js'
import {
    ClassroomCollectionDefinition,
    type FamilyClassroomStore
} from './stores/classroom-store.js'
import { ChildCollectionDefinition, type FamilyChildStore } from './stores/child-store.js'
import { EventCollectionDefinition, type FamilyEventStore } from './stores/event-store.js'
import { FeatureCollectionDefinition, type FamilyFeatureStore } from './stores/feature-store.js'
import { GuardianCollectionDefinition, type FamilyGuardianStore } from './stores/guardian-store.js'
import {
    LocalEncryptionEnrollmentCollectionDefinition,
    FamilyLocalEncryptionStoreAdapter,
    type FamilyLocalEncryptionStore
} from './stores/local-encryption-enrollment-store.js'
import {
    FamilyLibraryCatalogMetadataStore,
    LibraryCatalogMetadataCollectionDefinition
} from './stores/library-catalog-metadata-store.js'
import {
    FamilyLibraryStoryStore,
    LibraryStoryCollectionDefinition
} from './stores/library-story-store.js'
import {
    FamilyLibraryVideoStore,
    LibraryVideoCollectionDefinition
} from './stores/library-video-store.js'
import {
    FamilyMessagingStore,
    MessagingDailyCollectionDefinition,
    MessagingMetadataCollectionDefinition
} from './stores/messaging-store.js'
import {
    FamilyUserContextStoreAdapter,
    UserContextCollectionDefinition,
    type FamilyUserContextStore
} from './stores/user-context-store.js'

export interface FamilyPersistence {
    getAuthenticationContextStore(): Promise<FamilyAuthenticationContextStore>
    getAttachmentMetadataStore(context: UserContext): Promise<FamilyAttachmentMetadataStore>
    getClassroomStore(context: UserContext): Promise<FamilyClassroomStore>
    getChildStore(context: UserContext): Promise<FamilyChildStore>
    getEventStore(context: UserContext): Promise<FamilyEventStore>
    getFeatureStore(context: UserContext): Promise<FamilyFeatureStore>
    getGuardianStore(context: UserContext): Promise<FamilyGuardianStore>
    getLocalEncryptionStore(context: UserContext): Promise<FamilyLocalEncryptionStore>
    getLibraryCatalogMetadataStore(): Promise<FamilyLibraryCatalogMetadataStore>
    getLibraryStoryStore(): Promise<FamilyLibraryStoryStore>
    getLibraryVideoStore(): Promise<FamilyLibraryVideoStore>
    getMessagingStore(context: UserContext): Promise<FamilyMessagingStore>
    getUserContextStore(): Promise<FamilyUserContextStore>
}

export type FamilyPersistenceOptions = {
    persistenceManager?: PersistenceManager
}

export class FamilyPersistenceService implements FamilyPersistence {
    readonly #persistenceManager: PersistenceManager

    public constructor(options: FamilyPersistenceOptions = {}) {
        this.#persistenceManager = options.persistenceManager ?? createFamilyPersistenceManager()
    }

    public async getAuthenticationContextStore(): Promise<FamilyAuthenticationContextStore> {
        const database = await this.#persistenceManager.global()
        const collection = await database.getCollection(AuthenticationContextCollectionDefinition)
        return new FamilyAuthenticationContextStoreAdapter(collection)
    }

    public async getAttachmentMetadataStore(
        context: UserContext
    ): Promise<FamilyAttachmentMetadataStore> {
        const database = await this.#getUserDatabase(context)
        return await database.getCollection(AttachmentMetadataCollectionDefinition)
    }

    public async getChildStore(context: UserContext): Promise<FamilyChildStore> {
        const database = await this.#getUserDatabase(context)
        return await database.getCollection(ChildCollectionDefinition)
    }

    public async getClassroomStore(context: UserContext): Promise<FamilyClassroomStore> {
        const database = await this.#getUserDatabase(context)
        return await database.getCollection(ClassroomCollectionDefinition)
    }

    public async getEventStore(context: UserContext): Promise<FamilyEventStore> {
        const database = await this.#getUserDatabase(context)
        return await database.getCollection(EventCollectionDefinition)
    }

    public async getFeatureStore(context: UserContext): Promise<FamilyFeatureStore> {
        const database = await this.#getUserDatabase(context)
        return await database.getCollection(FeatureCollectionDefinition)
    }

    public async getGuardianStore(context: UserContext): Promise<FamilyGuardianStore> {
        const database = await this.#getUserDatabase(context)
        return await database.getCollection(GuardianCollectionDefinition)
    }

    public async getLocalEncryptionStore(
        context: UserContext
    ): Promise<FamilyLocalEncryptionStore> {
        const database = await this.#getUserDatabase(context)
        const collection = await database.getCollection(
            LocalEncryptionEnrollmentCollectionDefinition
        )
        return new FamilyLocalEncryptionStoreAdapter(collection)
    }

    public async getLibraryStoryStore(): Promise<FamilyLibraryStoryStore> {
        const database = await this.#persistenceManager.global()
        const collection = await database.getCollection(LibraryStoryCollectionDefinition)
        return new FamilyLibraryStoryStore(collection)
    }

    public async getLibraryVideoStore(): Promise<FamilyLibraryVideoStore> {
        const database = await this.#persistenceManager.global()
        const collection = await database.getCollection(LibraryVideoCollectionDefinition)
        return new FamilyLibraryVideoStore(collection)
    }

    public async getLibraryCatalogMetadataStore(): Promise<FamilyLibraryCatalogMetadataStore> {
        const database = await this.#persistenceManager.global()
        const collection = await database.getCollection(LibraryCatalogMetadataCollectionDefinition)
        return new FamilyLibraryCatalogMetadataStore(collection)
    }

    public async getMessagingStore(context: UserContext): Promise<FamilyMessagingStore> {
        const database = await this.#getUserDatabase(context)
        const [dailyCollection, metadataCollection] = await Promise.all([
            database.getCollection(MessagingDailyCollectionDefinition),
            database.getCollection(MessagingMetadataCollectionDefinition)
        ])
        return new FamilyMessagingStore(dailyCollection, metadataCollection)
    }

    public async getUserContextStore(): Promise<FamilyUserContextStore> {
        const database = await this.#persistenceManager.global()
        const collection = await database.getCollection(UserContextCollectionDefinition)
        return new FamilyUserContextStoreAdapter(collection)
    }

    async #getUserDatabase(context: UserContext): Promise<PersistenceDatabase> {
        return await this.#persistenceManager.forUser(context.person.key)
    }
}

export function createFamilyPersistence(options: FamilyPersistenceOptions = {}): FamilyPersistence {
    return new FamilyPersistenceService(options)
}
